// Org-aware React hook. Calls GET /api/org once per mount, exposes
// the org, the calling user's member record, group list and a few
// mutating helpers used by the Settings → Organization tab.
//
// All mutations refetch on success so the UI stays canonical without
// hand-rolled optimistic state.
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const API = getBackendBaseUrl();

function _bearer(session) {
  const token = session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Defensive read — Emergent RUM occasionally drains the response body
// before we can parse it. Always clone first.
async function _readJson(r) {
  try { return await r.clone().json(); }
  catch {
    try { return await r.json(); } catch { return null; }
  }
}
async function _readText(r) {
  try { return await r.clone().text(); }
  catch { return r.statusText || `HTTP ${r.status}`; }
}

export function useOrganization() {
  const { session } = useAuth();
  const token = session?.access_token;

  const [org, setOrg] = useState(null);
  const [member, setMember] = useState(null);
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const r = await fetch(`${API}/api/org`, { headers: _bearer(session) });
      if (r.status === 404) {
        // User has no org yet — solo account.
        setOrg(null); setMember(null); setGroups([]); setMembers([]);
        setLoading(false);
        return;
      }
      if (!r.ok) throw new Error(await _readText(r));
      const data = await _readJson(r);
      setOrg(data?.organization || null);
      setMember(data?.member || null);
      setGroups(data?.groups || []);

      // Pull members separately so we don't bloat the org bootstrap.
      const mr = await fetch(`${API}/api/org/members`, { headers: _bearer(session) });
      if (mr.ok) setMembers(await _readJson(mr) || []);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  // ── Members
  const inviteMember = async (email, role) => {
    const r = await fetch(`${API}/api/org/members/invite`, {
      method: 'POST',
      headers: { ..._bearer(session), 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role }),
    });
    if (!r.ok) throw new Error(await _readText(r));
    await refresh();
    return await _readJson(r);
  };

  const updateMemberRole = async (userId, role) => {
    const r = await fetch(`${API}/api/org/members/${encodeURIComponent(userId)}/role`, {
      method: 'PATCH',
      headers: { ..._bearer(session), 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    if (!r.ok) throw new Error(await _readText(r));
    await refresh();
  };

  const removeMember = async (userId) => {
    const r = await fetch(`${API}/api/org/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: _bearer(session),
    });
    if (!r.ok) throw new Error(await _readText(r));
    await refresh();
  };

  // ── Groups
  const createGroup = async (name, memberIds) => {
    const r = await fetch(`${API}/api/org/groups`, {
      method: 'POST',
      headers: { ..._bearer(session), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, member_ids: memberIds }),
    });
    if (!r.ok) throw new Error(await _readText(r));
    await refresh();
    return await _readJson(r);
  };

  const updateGroup = async (groupId, { name, memberIds }) => {
    const body = {};
    if (name !== undefined) body.name = name;
    if (memberIds !== undefined) body.member_ids = memberIds;
    const r = await fetch(`${API}/api/org/groups/${encodeURIComponent(groupId)}`, {
      method: 'PATCH',
      headers: { ..._bearer(session), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(await _readText(r));
    await refresh();
  };

  const deleteGroup = async (groupId) => {
    const r = await fetch(`${API}/api/org/groups/${encodeURIComponent(groupId)}`, {
      method: 'DELETE',
      headers: _bearer(session),
    });
    if (!r.ok) throw new Error(await _readText(r));
    await refresh();
  };

  return {
    org, member, members, groups,
    hasOrg: !!org,
    role: member?.role,
    loading, error,
    refresh,
    inviteMember, updateMemberRole, removeMember,
    createGroup, updateGroup, deleteGroup,
  };
}
